import React, { createContext, useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import ListingServices from '../Services/Consumers/ListingServices'
import { FormattedMessage, useIntl } from 'react-intl'
import { filterData, handleNotificationAlert } from '../utils/helpers'
import { arrangeListingData, ListingHeaders } from '../utils/ListingHelpers'
import { ListingParamsPayload } from '../Services/Payloads/AdminPayload'
import { useKeycloak } from '@react-keycloak/web'
import { getActionSubmitPayload } from '../Services/Payloads/ListingPayload'
import EntityServices from '../Services/Consumers/EntityServices'
import { AuthContext } from './AuthContext'

const ListingContext = createContext({})

const ListingProvider = ({ entityName, locale, listArr, filter, rowsPerPage, listingPaging, initListArr = [], initFilter, codes, codeType, codeTypeChanged, reCallToGetData = -1, codePagination, disableDefaultSearch, children }) => {
  const intl = useIntl()
  let reCallInterval = useRef(null)
  const { keycloak } = useKeycloak()
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isMounted, setIsMounted] = React.useState(false)
  React.useEffect(() => {
    if (!!entityName) {
      if (!disableDefaultSearch && !codes && !initFilter) {
        // if (!codes && !initFilter) {
        setListingPage(0)
      }
      setListingHeader(ListingHeaders[entityName])
      setIsMounted(true)
    }
    return () => !!reCallInterval?.current && clearInterval(reCallInterval.current)
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const [listingRowsPerPage, setListingRowsPerPage] = React.useState(rowsPerPage || 10)
  const [listingHeader, setListingHeader] = React.useState([])
  const [listingLoading, setListingLoading] = React.useState(false)
  const [listingData, setListingData] = React.useState([])
  const [currentSelectedRow, setCurrentSelectedRow] = React.useState({})
  const [currentDialogAction, setCurrentDialogAction] = React.useState('')
  const [listingPage, setListingPage] = React.useState(-1)
  const [listingCount, setListingCount] = React.useState(0)
  const [needRefreshListingData, setNeedRefreshListingData] = React.useState(false)
  const [listingFiltersBackup, setListingFiltersBackup] = React.useState(filter || null)
  const [listingRecallCount, setListingRecallCount] = React.useState(reCallToGetData)
  const [listingFinishRecall, setListingFinishRecall] = React.useState(false)
  const [deleteItemLoading, setDeleteItemLoading] = React.useState(false)
  const [entityData, setEntityData] = React.useState({})

  const { userDataLoading } = useContext(AuthContext)

  React.useEffect(() => {
    if (!!entityName && !!isMounted) {
      if (!codes && !initFilter) {
        setListingCount(0)
        setListingData([])
        setListingFiltersBackup({ ...filter })
      }
      setListingHeader(ListingHeaders[entityName])
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityName])

  // Get Entity Data based on taxpayer data
  React.useEffect(() => {
    if (!!entityName && !!isMounted && !userDataLoading) {
      getEntityData()
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityName, userDataLoading, isMounted])

  const handleListingFilterSubmit = (values, FormikActions, clear) => {
    // if(!!clear){
    //   setListingPage(-1)
    //   setListingCount(0)
    //   setListingData([])
    // }else{
    // if (!!values && !!Object.keys(values).length) {
    setListingFiltersBackup({ ...filter, ...(values || {}) })
    // }
    // }
  }
  const handleNeedRefreshListingData = (value) => {
    setNeedRefreshListingData(!!value)
  }

  React.useEffect(() => {
    if (!!listingFiltersBackup || !!listArr) {
      if (listingPage === 0) {
        getListingData()
      }
      setListingPage(0)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [listingFiltersBackup, listArr])

  React.useEffect(() => {
    if (!!filter && !!Object.keys(filter || {}).length) {
      setListingFiltersBackup(filter)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter])

  React.useEffect(() => {
    if (!!initFilter && !!Object.keys(initFilter || {}).length) {
      setListingFiltersBackup(initFilter)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [initFilter])

  React.useEffect(() => {
    if (listingRecallCount === 0) {
      clearInterval(reCallInterval.current)
      setListingFinishRecall(true)
      setListingLoading(false)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [listingRecallCount])

  React.useEffect(() => {
    if (listingPage > -1) {
      getListingData()
      if (listingRecallCount > 0) {
        reCallInterval.current = setInterval(getListingData, 5000)
      }
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [listingPage, listingRowsPerPage])

  const refreshListingData = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setListingLoading(true)
    setListingRecallCount(reCallToGetData)
    setListingPage(-1)
    setTimeout(() => setListingPage(0), 100)
  }

  React.useEffect(() => {
    // if (!!codeTypeChanged && !!isMounted) {
    if (!!codeTypeChanged && !!isMounted) {
      if (!disableDefaultSearch) {
        if (codeType === codeTypeChanged) {
          if (listingPage !== 0) {
            setListingPage(0)
          } else {
            getListingData()
          }
        } else {
          if (!!listingFiltersBackup) {
            getListingData()
          } else {
            setListingData([])
          }
        }
      } else {
        setListingData([])
      }
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [codeTypeChanged])

  const handleChangeListingPage = (newPage) => {
    setListingPage(newPage)
  }

  const handleChangeListingRowsPerPage = (newRows) => {
    setListingRowsPerPage(+newRows)
    setListingPage(0)
  }

  // https://api-portal.preprod.invoicing.eta.gov.eg/api/v1/codetypes/codes/my?CodeTypeID=9&ItemCode=EG-122222&SubmitExpiredCode=true&Ps=100
  // https://api-portal.preprod.invoicing.eta.gov.eg/api/v1/codetypes/2/codes?CodeLookupValue=122222&ApplyMinChoiceLevel=true&Ps=100
  const getListingData = () => {
    if (!!keycloak?.authenticated) {
      if (!!listArr) {
        setListingCount(listArr?.length)
        setListingData(arrangeListingData(entityName, listArr))
      } else {
        setListingLoading(true)
        ListingServices.getListingData(entityName, filterData(ListingParamsPayload(entityName, listingPage + 1, listingRowsPerPage, listingPaging, listingFiltersBackup, codePagination)),
          intl.formatMessage({ id: `listing.Error.${entityName}` }), codeTypeChanged, true, !['codesSearch', 'codesUsage', 'codeRequests', 'notification', 'invoicePackages'].includes(entityName), ['codesSearch', 'codesUsage', 'codeRequests', 'notification', 'invoicePackages'].includes(entityName) ? intl.formatMessage({ id: `listing.Error.401.${entityName}` }) : null).then((result) => {
            if (!!result && !!result.data && (!!result.data.content || !!result.data.result || !!result.data.certificates || ['drafts', 'templates', 'lateSubmission'].includes(entityName))) {
              if (entityName === 'userCertificates') {
                setListingCount(result.data.certificates?.length)
                setListingData(result.data.certificates)
              } else if (entityName === 'lateSubmission') {
                setListingCount(result.data?.lateSubmissionRequests?.length || 0)
                setListingData(result.data?.lateSubmissionRequests || [])
              } else {
                let newData = arrangeListingData(entityName, !!['drafts', 'templates'].includes(entityName) ? (result.data?.result || result.data) : (result.data?.result || result.data?.content), '', locale)
                setListingCount(!!['drafts', 'templates'].includes(entityName) ? (result.data?.metaData?.totalCount || result.data?.metadata?.totalCount || result.data?.length) : (result.data?.metadata?.totalCount || result.data?.totalElements))
                if (!!initListArr?.length && codeType === codeTypeChanged) {
                  let initialData = arrangeListingData(entityName, initListArr)
                  let ids = new Set(initialData.map(d => d.id))
                  newData = [...initialData, ...arrangeListingData(entityName, result.data.result).filter(d => !ids.has(d.id))]
                }
                setListingData(newData)
              }
              if (reCallInterval?.current) {
                clearInterval(reCallInterval.current)
                setListingLoading(false)
              }
            } else {
              if (listingRecallCount > 0) {
                setListingRecallCount((prevState) => prevState - 1)
              } else {
                setListingLoading(false)
              }
            }
          }).then(() => {
            if (listingRecallCount < 1) {
              setListingLoading(false)
            }
          })
      }
    }
  }

  const getEntityData = () => {
    if (entityName === 'lateSubmission') {
      EntityServices.getLateSubmissionRemainingQuota().then((result) => {
        if (!!result && !!result?.data) {
          setEntityData(result?.data)
        } else {
          handleNotificationAlert({
            type: 'defence',
            message: intl.formatMessage({ id: `lateSubmission.Error.remainingQuota`}),
            color: 'error'
          }, 'error')
        }
      })
    }
  }

  const getEntityName = (entityName, currentDialogAction, requestType) => {
    let entity = entityName
    switch (entityName) {
      case 'codesUsage':
      case 'codeRequests':
        if (currentDialogAction === 'create') {
          switch (requestType) {
            case 'reuse':
              entity = 'codesUsageCreate'
              break
            case 'create':
              entity = 'codeUsageEdit'
              break
            default:
              break
          }
        }
        break
      case 'lateSubmission':
        entity = 'lateSubmissionCreate'
        break
      default:
        break
    }
    return entity
  }
  const dialogActionSubmit = (values, FormikActions, closeConfirmDialog, itemName, requestType, customID) => {
    if (!!keycloak?.authenticated) {
      let newEntityName = getEntityName(entityName, currentDialogAction, requestType)
      if (currentDialogAction === 'delete') {
        setDeleteItemLoading(true)
      }
      ListingServices.handleDialogSubmit(newEntityName, currentDialogAction, setDeleteItemLoading, FormikActions,
        filterData(getActionSubmitPayload(newEntityName, currentDialogAction, requestType, values)), customID ?? (currentSelectedRow?.id?.toString() || ''),
        intl.formatMessage({ id: `listing.Error.${newEntityName}.${currentDialogAction}${requestType ? `.${requestType}` : ''}` }, { name: itemName }),
        null, ['codesSearch', 'codesUsage', 'codeRequests'].includes(entityName) ? intl.formatMessage({ id: `listing.Error.401.action.${entityName}` }) : null).then((result) => {
          if (!!result && ((!!result.data && !!result.data.data) || ['200', '201', '204'].includes(result.status.toString()))) {
            if (['codesUsageCreate', 'codeUsageEdit'].includes(newEntityName) && result?.data?.passedItemsCount === 0) {
              let message = <FormattedMessage
                id={`listing.Error.${newEntityName}.${currentDialogAction}${requestType ? `.${requestType}` : ''}`}
                values={{ name: itemName }} />
              let messagesList = []
              if (!!result?.data?.failedItems?.length) {
                result?.data?.failedItems.forEach(failedItem => failedItem?.errors?.forEach(msg => messagesList.push(msg)))
              }
              if (!!messagesList?.length) {
                messagesList.forEach(msg => {
                  handleNotificationAlert({
                    type: 'defence',
                    message: msg,
                    color: 'error'
                  }, 'error')
                })
              } else {
                handleNotificationAlert({
                  type: 'defence',
                  message: message,
                  color: 'error'
                }, 'error')
              }
            } else {
              let message = entityName === 'lateSubmission' && !!result?.data?.responseMessage
                ? result?.data?.responseMessage
                : intl.formatMessage({ id: `listing.Success.${newEntityName}.${currentDialogAction}${requestType ? `.${requestType}` : ''}` }, { name: itemName })
              handleNotificationAlert({
                type: 'done',
                message: message,
                color: 'success'
              }, 'success')
            }
            if (!!FormikActions) {
              FormikActions.setSubmitting(false)
            }

            /** Close Dialog**/
            if (!!closeConfirmDialog) {
              closeConfirmDialog()
              setTimeout(() => cancelDialog(), 200)
            } else {
              cancelDialog()
            }
            /** Reload Listing after Add/Edit/Delete**/
            if (!(['codesUsageCreate', 'codeUsageEdit'].includes(newEntityName) && result?.data?.passedItemsCount === 0)) {
              setTimeout(() => {
                if (listingPage === 0) {
                  getListingData()
                }
                setListingPage(0)
              }, 500)
            }
          }
        }).then(() => {
          setDeleteItemLoading(false)
          if (!!FormikActions) {
            FormikActions.setSubmitting(false)
          }
        })
    }
  }


  const openDialog = (type, selectedRow, id, handleMenuClose) => {
    if (!!handleMenuClose) {
      handleMenuClose()
    }
    setCurrentSelectedRow(selectedRow || {})
    setCurrentDialogAction(type || 'create')
    setIsDialogOpen(!isDialogOpen)
  }

  const cancelDialog = (forceRefresh, clearCodeData) => {
    setIsDialogOpen(false)
    if (!!clearCodeData) {
      clearCodeData()
    }
    if (!!needRefreshListingData || !!forceRefresh) {
      setNeedRefreshListingData(false)
      setTimeout(() => {
        if (listingPage === 0) {
          getListingData()
        }
        setListingPage(0)
      }, 500)
    }
  }

  const [downloadLoading, setDownloadLoading] = React.useState(false)
  const [downloadLoadingProgress, setDownloadLoadingProgress] = React.useState(0)
  const downloadEntity = (id) => {
    if (!!keycloak?.authenticated) {
      let extension = 'zip'
      let blobType = 'application/octet-stream'
      setDownloadLoading(true)
      ListingServices.getDownloadData(`${entityName}Download`, id, setDownloadLoadingProgress, intl.formatMessage({ id: `listing.Error.${entityName}.Download` }, { id: id })).then((result) => {
        if (!!result && !!result.data) {
          let data = result.data
          const blob = new Blob([data], { type: blobType, encoding: 'UTF-8' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const contentDisposition = result.headers['content-disposition']
          let fileName = `${intl.formatMessage({ id: `listing.${entityName}.download.fileName` })}-${id}.${extension}`
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="(.+)"/)
            if (fileNameMatch.length === 2)
              fileName = fileNameMatch[1]
          }
          link.setAttribute('download', fileName) //or any other extension
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        }
      }).then(() => {
        setDownloadLoading(false)
        setTimeout(() => setDownloadLoadingProgress(0), 200)
      })
    }
  }

  return (
    <ListingContext.Provider value={{
      entityName,
      entityData,

      listingData,
      listingHeader,
      listingLoading,
      listingFinishRecall,
      reCallInterval,
      refreshListingData,

      listingCount,
      listingRowsPerPage,
      listingPage,
      handleChangeListingPage,
      handleChangeListingRowsPerPage,


      isDialogOpen,
      openDialog,
      cancelDialog,

      currentSelectedRow,
      currentDialogAction,
      handleListingFilterSubmit,

      dialogActionSubmit,
      handleNeedRefreshListingData,

      downloadEntity,
      downloadLoadingProgress,
      downloadLoading,

      deleteItemLoading

    }}>
      {children}
    </ListingContext.Provider>
  )
}

ListingProvider.propTypes = {
  entityName: PropTypes.string,
  children: PropTypes.any
}
const ListingConsumer = ListingContext.Consumer

export { ListingProvider, ListingConsumer }
